/**
 * Sprint 40 — Accesibilidad: escala de fuente + tema.
 * Sprint 48 — Familia de fuente + slider continuo de tamaño.
 *
 * `EscalaFuente` es ahora un string numérico (ej. "0.75", "1.0").
 * Los valores legacy 'normal'/'comoda'/'grande' se convierten automáticamente.
 *
 * Sin Google Fonts (CSP solo permite font-src 'self' data:).
 */

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
export type EscalaFuente = string;   // factor numérico como string, ej. "0.75"
export type TemaUI = 'claro' | 'alto-contraste';
export type FuenteFamilia = 'inter' | 'segoe' | 'calibri';

// ---------------------------------------------------------------------------
// Escala de fuente — slider continuo
// ---------------------------------------------------------------------------
export const ESCALA_MIN = 0.65;
export const ESCALA_MAX = 1.25;
export const ESCALA_STEP = 0.05;
export const ESCALA_DEFAULT = '1';   // 100% — valor neutral al resetear

/** Puntos de referencia con etiqueta para marcar el slider */
export const ESCALA_PUNTOS: Array<{ factor: number; label: string }> = [
  { factor: 0.65,   label: 'Mínima'   },
  { factor: 0.75,   label: 'Pequeña'  },
  { factor: 0.875,  label: 'Reducida' },
  { factor: 1.0,    label: 'Normal'   },
  { factor: 1.125,  label: 'Cómoda'   },
  { factor: 1.25,   label: 'Grande'   },
];

/** Convierte cualquier valor almacenado (legacy o numérico) a EscalaFuente. */
const LEGACY_MAP: Record<string, number> = {
  normal: 1.0,
  comoda: 1.125,
  grande: 1.25,
};

export function parseEscala(valor: string | null | undefined): EscalaFuente {
  if (!valor) return ESCALA_DEFAULT;
  if (LEGACY_MAP[valor] !== undefined) return String(LEGACY_MAP[valor]);
  const n = parseFloat(valor);
  if (!isNaN(n) && n >= ESCALA_MIN - 0.01 && n <= ESCALA_MAX + 0.01) {
    return String(Math.round(n * 1000) / 1000);
  }
  return ESCALA_DEFAULT;
}

/** Extrae el factor numérico desde un EscalaFuente. */
export function factorEscala(escala: EscalaFuente): number {
  if (LEGACY_MAP[escala] !== undefined) return LEGACY_MAP[escala];
  const n = parseFloat(escala);
  if (isNaN(n)) return 1.0;
  return Math.max(ESCALA_MIN, Math.min(ESCALA_MAX, n));
}

/** Devuelve la etiqueta del punto más cercano, o el porcentaje si no coincide. */
export function escalaLabel(escala: EscalaFuente): string {
  const f = factorEscala(escala);
  const punto = ESCALA_PUNTOS.find((p) => Math.abs(p.factor - f) < 0.001);
  if (punto) return punto.label;
  return `${Math.round(f * 100)}%`;
}

/** Ctrl + + : sube un paso, tope en ESCALA_MAX. */
export function escalaSiguiente(actual: EscalaFuente): EscalaFuente {
  const f = factorEscala(actual);
  const next = Math.round((f + ESCALA_STEP) * 1000) / 1000;
  return String(Math.min(ESCALA_MAX, next));
}

/** Ctrl + - : baja un paso, tope en ESCALA_MIN. */
export function escalaAnterior(actual: EscalaFuente): EscalaFuente {
  const f = factorEscala(actual);
  const prev = Math.round((f - ESCALA_STEP) * 1000) / 1000;
  return String(Math.max(ESCALA_MIN, prev));
}

// ---------------------------------------------------------------------------
// Tema
// ---------------------------------------------------------------------------
export const TEMA_LABEL: Record<TemaUI, string> = {
  claro: 'Claro',
  'alto-contraste': 'Alto contraste',
};

export function parseTema(valor: string | null | undefined): TemaUI {
  if (valor === 'alto-contraste') return 'alto-contraste';
  return 'claro';
}

// ---------------------------------------------------------------------------
// Familia de fuente
// ---------------------------------------------------------------------------
export const FUENTE_FAMILIA_STACK: Record<FuenteFamilia, string> = {
  inter:   "'Inter', system-ui, -apple-system, sans-serif",
  segoe:   "'Segoe UI', system-ui, sans-serif",
  calibri: "Calibri, 'Gill Sans', 'Trebuchet MS', sans-serif",
};

export const FUENTE_FAMILIA_LABEL: Record<FuenteFamilia, string> = {
  inter:   'Inter / Sistema',
  segoe:   'Segoe UI',
  calibri: 'Calibri',
};

export const FUENTE_FAMILIA_DESCRIPCION: Record<FuenteFamilia, string> = {
  inter:   'Predeterminada · moderna y balanceada',
  segoe:   'Compacta · nativa de Windows',
  calibri: 'Muy compacta · ideal para pantalla pequeña',
};

export function parseFuenteFamilia(valor: string | null | undefined): FuenteFamilia {
  if (valor === 'segoe' || valor === 'calibri') return valor;
  return 'inter';
}

// ---------------------------------------------------------------------------
// Aplicar al DOM
// ---------------------------------------------------------------------------
/**
 * Aplica escala, tema y fuente al elemento `<html>` vía CSS vars + data-attrs.
 * Idempotente.
 */
export function aplicarUIPrefs(escala: EscalaFuente, tema: TemaUI, fuente: FuenteFamilia): void {
  const root = document.documentElement;
  root.style.setProperty('--font-scale', String(factorEscala(escala)));
  root.style.setProperty('--font-family', FUENTE_FAMILIA_STACK[fuente]);
  root.setAttribute('data-tema', tema);
  root.setAttribute('data-escala-fuente', escala);
  root.setAttribute('data-fuente-familia', fuente);
}
